import type {ReactElement, ReactNode} from 'react';
import {useEffect, useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize, TextType} from '@Components/text';
import {hideMessageGrowl, removeMessageGrowl, updateMessageGrowl, updateMessageGrowlProgress} from '@Components/message-growl/message-growl-reducer';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ShowMessageGrowlProps} from './message-growl.types';
import {GROWL_TYPE} from './message-growl.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './message-growl.module.scss';
import {useIsMounted} from '@Hooks/useIsMounted';
import {useIsTimelineButtonVisible} from '@Hooks/poster-editor/useIsTimelineButtonVisible';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {MessageGrowlItem} from './message-growl-reducer';

export function MessageGrowl(): ReactElement<any> {
  const growls = useAppSelector((state) => {
    return state.messageGrowl.growls;
  });
  const message = growls.length > 0 ? growls[growls.length - 1] : null;
  const loadingToast = useAppSelector((state) => {
    return state.loadingToast;
  });
  const currentLoading = useAppSelector((state) => {
    return state.loadingToast.currentLoading;
  });
  const isTimelineButtonVisible = useIsTimelineButtonVisible();
  const isDesktopTimelineToggleOn = useAppSelector((state) => {
    return state.posterEditor.isDesktopTimelineToggleOn;
  });
  const isSaveReminderDisplaying = useAppSelector((state) => {
    return state.saveReminder.show;
  });
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });
  const dispatch = useAppDispatch();
  const hasMounted = useIsMounted();
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (removeTimerRef.current) {
      clearTimeout(removeTimerRef.current);
      removeTimerRef.current = null;
    }

    if (message?.show) {
      hideTimerRef.current = setTimeout(() => {
        dispatch(hideMessageGrowl(message.key));
        removeTimerRef.current = setTimeout(() => {
          dispatch(removeMessageGrowl(message.key));
        }, 500);
      }, message.interval);
    }

    return (): void => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (removeTimerRef.current) {
        clearTimeout(removeTimerRef.current);
      }
    };
  }, [dispatch, message?.show, message?.key, message?.interval, message?.type]);

  const getLatestGrowl = (): MessageGrowlItem => {
    return message ?? {text: '', show: false, interval: 0, type: GROWL_TYPE.SUCCESS, key: '', showCloseIcon: true, icon: '', dangerouslySetInnerHTML: false};
  };

  const growl = getLatestGrowl();

  const getLeftBarBackgroundColor = (): string => {
    switch (growl.type) {
      case GROWL_TYPE.DANGER:
        return styles.leftBarDanger;
      case GROWL_TYPE.PREMIUM:
        return styles.leftBarPremium;
      case GROWL_TYPE.SECONDARY:
        return styles.leftBarSecondary;
      case GROWL_TYPE.NEUTRAL:
        return styles.leftBarNeutral;
      case GROWL_TYPE.SUCCESS:
      default:
        return styles.leftBarSuccess;
    }
  };

  const hasProgress = growl.progress !== undefined;

  const getClasses = (): string => {
    let loadingToastClass = currentLoading.show ? styles.displayingLoadingToast : '';
    if (currentLoading.show && currentLoading.progress) {
      loadingToastClass = styles.displayingLoadingToastWithProgress;
    }
    return `${styles.container} flex-row-justify-center radius-4 content-body spacing-p-r-2 _fit-width _fit-height
            ${growl.show ? styles.in : styles.out}
            ${hasMounted.current ? styles.doAnimate : ''}
            ${isSaveReminderDisplaying || loadingToast.isSaveReminderDisplaying ? styles.displayingSaveReminder : ''}
            ${isTimelineButtonVisible || isDesktopTimelineToggleOn || loadingToast.isProgressBarDisplaying ? styles.displayingSeekbar : ''}
            ${loadingToastClass}
            ${hasProgress ? styles.hasProgress : ''}`;
  };

  const getBottom = (): string => {
    let val = 0;
    const DEFAULT_GAP_OR_PADDING = 16;
    const DEFAULT_BOTTOM_BAR_HEIGHT_WITH_PADDING = 88;
    const DEFAULT_SAVE_REMINDER_HEIGHT = 137;
    if (isDesktopTimelineToggleOn) {
      val = bottomBarHeight ? bottomBarHeight + DEFAULT_GAP_OR_PADDING : DEFAULT_BOTTOM_BAR_HEIGHT_WITH_PADDING;
    } else if (isTimelineButtonVisible) {
      val = DEFAULT_BOTTOM_BAR_HEIGHT_WITH_PADDING;
    } else {
      val = DEFAULT_GAP_OR_PADDING;
    }

    if (isSaveReminderDisplaying || loadingToast.isSaveReminderDisplaying) {
      val += DEFAULT_SAVE_REMINDER_HEIGHT + DEFAULT_GAP_OR_PADDING;
    }

    return `${val}px`;
  };

  const getCircularLoader = (): ReactNode => {
    if (!hasProgress) {
      return null;
    }

    return (
      <div className={`spacing-p-l-3 flexbox flex-vertical-center`}>
        <CircularProgressLoader size={LOADER_SIZE.XSMALL} />
      </div>
    );
  };

  const getPercentageText = (): ReactNode => {
    if (!hasProgress) {
      return null;
    }

    const progressValue = Math.round(growl.progress ?? 0);

    return <Text className={styles.percentageText} size={TextSize.SMALL} val={`(${progressValue}%)`} />;
  };

  return (
    <div
      style={{
        bottom: getBottom(),
      }}
      className={getClasses()}
    >
      <div className={`${styles.leftBar} ${getLeftBarBackgroundColor()}`} />
      <div className="flexbox spacing-p-t-2 spacing-p-b-2">
        {growl.type === GROWL_TYPE.DANGER ? (
          <div className="_fit-width _fit-height">
            <Icon icon="icon-exclamation-triangle" className="spacing-p-l-3 content-danger" type={IconType.NONE} size={IconSize.SIZE_ICON_16} shape={IconShape.NONE} />
          </div>
        ) : null}

        {getCircularLoader()}

        <div className={`spacing-p-l-3 flexbox flex-vertical-center flex-v-row`}>
          <Text className={growl.subText ? '' : `${styles.text} spacing-p-l-3`} size={TextSize.SMALL} val={growl.text} bold={!!growl.subText} dangerouslySetInnerHTML />
          {growl.subText ? <Text type={TextType.BODY} size={TextSize.XSMALL} val={growl.subText} /> : null}
        </div>

        {getPercentageText()}

        {growl.showCloseIcon ? (
          <div>
            <Icon
              icon="icon-close"
              type={IconType.GHOST}
              shape={IconShape.SQUARE}
              size={IconSize.SIZE_ICON_16}
              onClick={(): void => {
                dispatch(hideMessageGrowl(growl.key));
                removeTimerRef.current = setTimeout(() => {
                  dispatch(removeMessageGrowl(growl.key));
                }, 500);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const showMessageGrowl = (message: ShowMessageGrowlProps): void => {
  window.PMW.redux.store.dispatch(
    updateMessageGrowl({
      key: message.key,
      text: message.text,
      type: message.type,
      interval: message.interval,
      showCloseIcon: message.showCloseIcon,
      icon: message.icon,
      dangerouslySetInnerHTML: message.dangerouslySetInnerHTML,
      subText: message.subText,
      progress: message.progress,
    })
  );
};

export const updateGrowlProgress = (key: string, progress: number): void => {
  window.PMW.redux.store.dispatch(updateMessageGrowlProgress({key, progress}));
};
